import useAPI from "@toothfairy/shared-api/useApi";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { AppText } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import envConfig from "../../../envConfig";
import Files from "../../API/Files";
import useWorkspaces from "../../Hooks/useWorkspaces";
import ToothFairyButton from "../ToothFairyComponents/ToothFairyButton";
import { Platform, View, useWindowDimensions } from "react-native";
import ToothFairyModal from "../ToothFairyComponents/ToothFairyModal";
import Constants from "../../Constants";
import { isLargeScreen } from "../../Screens/MainViewContainer";
import trainingAPI from "../../API/Training/trainingAPI";
import DelayedRender from "../DelayRenderer";
import LoadingPlaceholder from "../LoadingPlaceholder";
import { isElectronRunning } from "../../../App";
import hljs from "highlight.js";

const GenericViewer = ({
  path,
  onPDFDocumentLoad = () => {},
  documentId,
  isPDFLoaded,
}) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState(null);
  const AppToasty = AppToast.useToast();
  const { height } = useWindowDimensions();
  const isTabularFile =
    path?.split(".").pop() === "xlsx" ||
    path?.split(".").pop() === "csv" ||
    path?.split(".").pop() === "xlsm";
  const [highlightedCode, setHighlightedCode] = useState("");
  const codeFilesExtensions = [
    "py",
    "java",
    "yaml",
    "yml",
    "sql",
    "sh",
    "php",
    "js",
    "ts",
    "tsx",
    "jsx",
    "csharp",
    "rb",
  ];
  const isCode = codeFilesExtensions.includes(path?.split(".").pop());

  const [content, setContent] = useState("");
  const { currentTheme, mode } = AppTheme.useTheme();
  const { getActiveWorkspace } = useWorkspaces();
  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);
  const {
    loading: fileMetaDataSavingInProgress,
    apiRequest: updateDocumentFileMetaData,
  } = useAPI(trainingAPI.updateTrainingData, null, () => {});
  const [metaDescription, setMetaDescription] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [chunkingModalOpen, setChunkingModalOpen] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const {
    data: getAPIResult,
    error: getAPIError,
    loading: getAPIInProgress,
    apiRequest: getAPIRequest,
    lastUpdate: lastrequestTime,
  } = useAPI(trainingAPI.getDocumentById, envConfig);
  const formValidationSchema = AppForms.yup.object().shape({
    file_description: AppForms.yup
      .string()
      .nullable()
      .label(t("documents.genericViewer.fileDescription"))
      .max(500),
  });
  const fetchDocumentData = async (documentId) => {
    try {
      const response = await getAPIRequest(
        documentId,
        [
          "file_description",
          "workspaceid",
          "tabular_chunk_size",
          "tabular_overlap",
          "tabular_separator",
        ],
        getActiveWorkspace()?.id,
      );
      if (response?.data?.length > 0 && response?.data[0])
        setInitialValues({
          file_description: response?.data[0]?.file_description,
          tabular_chunk_size: response?.data[0]?.tabular_chunk_size || 10,
          tabular_overlap: response?.data[0]?.tabular_overlap || 1,
          tabular_separator: response?.data[0]?.tabular_separator || ",",
        });
    } catch (error) {
      console.error("Error while fetching document data", error);
    }
  };
  const mapPathToLanguage = (path) => {
    switch (path?.split(".").pop()) {
      case "py":
        return "python";
      case "java":
        return "java";
      case "yaml":
        return "yaml";
      case "yml":
        return "yaml";
      case "sql":
        return "sql";
      case "sh":
        return "bash";
      case "php":
        return "php";
      case "js":
        return "javascript";
      case "ts":
        return "typescript";
      case "csharp":
        return "csharp";
      case "rb":
        return "ruby";
      default:
        return "javascript";
    }
  };
  useEffect(() => {
    const highlightCode = () => {
      const code = hljs.highlight(content, {
        language: mapPathToLanguage(path),
        ignoreIllegals: true,
      }).value;
      setHighlightedCode(code);
    };

    highlightCode();
    // Cleanup function
    return () => {
      setHighlightedCode("");
    };
  }, [path, mode, content]);
  useEffect(() => {
    if (documentId && getActiveWorkspace()?.id) {
      fetchDocumentData(documentId);
    }
  }, [documentId, getActiveWorkspace()?.id]);
  const handleChunkingFormSubmit = async (formValues) => {
    try {
      await updateDocumentFileMetaData({
        fields: {
          id: documentId,
          workspaceid: getActiveWorkspace()?.id,
          tabular_chunk_size: formValues.tabular_chunk_size,
          tabular_overlap: formValues.tabular_overlap,
          tabular_separator: formValues.tabular_separator,
        },
      });
      AppToasty.show(t("toasts.chunkingUpdated"), {
        type: "success",
      });
      setModalOpen(false);
    } catch (error) {
      console.error("Error while updating the chunking configuration", error);
      AppToasty.show(t("toasts.chunkingUpdateFailed"), {
        type: "danger",
      });
    }
  };
  const handleFormSubmit = async (formValues) => {
    try {
      await updateDocumentFileMetaData({
        fields: {
          id: documentId,
          workspaceid: getActiveWorkspace()?.id,
          file_description: formValues.file_description,
        },
      });
      AppToasty.show(t("toasts.metadataUpdated"), {
        type: "success",
      });
      setModalOpen(false);
    } catch (error) {
      console.error("Error while updating meta description", error);
      AppToasty.show(t("toasts.metadataUpdateFailed"), {
        type: "danger",
      });
    }
  };
  const metaUpdateComponent = useMemo(() => {
    return (
      <ToothFairyModal
        modalStyle={{
          height: 525,
          width: "90%",
          maxWidth: isLargeScreen() ? 750 : 350,
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        isModalOpen={modalOpen}
        setIsModalOpen={setModalOpen}
        modalTitle={t("documents.genericViewer.updateMetaDescription")}
      >
        <AppForms.AppForm
          style={{
            minWidth: Platform.OS === "web" && isLargeScreen() ? 350 : 300,
            // maxWidth: 600,
            alignItems: "center",
          }}
          initialValues={initialValues}
          onSubmit={handleFormSubmit}
          validationSchema={formValidationSchema}
          enableReinitialize
        >
          <AppForms.AppFormField
            showTextInputLabel
            formFieldContainerStyle={{
              width: "100%",
            }}
            disabled={fileMetaDataSavingInProgress}
            wrapperInputStyle={{
              paddingTop: 10,
              marginBottom: 10,
            }}
            multiline={true}
            textContainer={{
              minHeight: 345,
              paddingVertical: 10,
              marginHorizontal: 20,
            }}
            textInputLabelStyle={{
              marginLeft: 20,
            }}
            label={t("documents.genericViewer.fileDescription")}
            placeholder={t(
              "documents.genericViewer.fileDescriptionPlaceholder",
            )}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="file_description"
            maxCharacters={500}
          />
          <AppForms.AppSubmitButton
            title={t("documents.genericViewer.update")}
            isDisabled={fileMetaDataSavingInProgress}
            awesomeIconColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.primary}
            btnContentStyle={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            style={[
              {
                height: Constants.appButtonHeight,
                width: "auto",
                shadowColor: currentTheme?.theme?.transparent,
                marginHorizontal: 10,
              },
            ]}
            // btnLabelStyle={{ fontSize: 16 }}
            wrapperStyle={[
              {
                width: "fit-content",
                minWidth: 150,

                height: Constants.appButtonHeight,
              },
            ]}
            // backgroundColor={currentTheme?.theme?.green}

            // awesomeIcon={AppAssets.icons.add_icon}
            // awesomeIconColor={currentTheme?.theme?.white}
          />
        </AppForms.AppForm>
      </ToothFairyModal>
    );
  }, [modalOpen, setModalOpen, fileMetaDataSavingInProgress, t]);

  const chunkingMetaComponent = useMemo(() => {
    return (
      <ToothFairyModal
        modalStyle={{
          height: path?.split(".").pop() === "csv" ? 408 : 340,
          width: "90%",
          maxWidth: isLargeScreen() ? 750 : 350,
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        isModalOpen={chunkingModalOpen}
        setIsModalOpen={setChunkingModalOpen}
        modalTitle={t("documents.genericViewer.updateChunkingConfiguration")}
      >
        <AppForms.AppForm
          style={{
            minWidth: Platform.OS === "web" && isLargeScreen() ? 350 : 300,
            // maxWidth: 600,
            alignItems: "center",
          }}
          initialValues={initialValues}
          onSubmit={handleChunkingFormSubmit}
          validationSchema={formValidationSchema}
          enableReinitialize
        >
          <AppForms.AppFormSlider
            step={1}
            maximumValue={50}
            minimumValue={1}
            label={t("documents.genericViewer.chunkingRows")}
            name="tabular_chunk_size"
            // showEditButtons={true}
            containerStyle={{
              width: 300,
              marginBottom: 10,
            }}
          />
          <AppForms.AppFormSlider
            step={1}
            maximumValue={5}
            minimumValue={1}
            label={t("documents.genericViewer.rowsOverlap")}
            name="tabular_overlap"
            // showEditButtons={true}
            containerStyle={{
              width: 300,
              marginBottom: 10,
            }}
          />
          {path?.split(".").pop() === "csv" && (
            <AppForms.AppFormField
              formFieldContainerStyle={{
                marginLeft: -85,
              }}
              textInputLabelStyle={{
                marginLeft: 0,
              }}
              showTextInputLabel
              label={t("documents.genericViewer.separator")}
              name="tabular_separator"
              maxCharacters={2}
              inputType="prompt"
              validationErrorStyle={{
                display: "none",
              }}
            />
          )}

          <AppText
            leftIcon={AppAssets?.icons?.info}
            wrapperStyle={{
              paddingHorizontal: 10,
              maxWidth: 460,
              marginBottom: 10,
            }}
            color={currentTheme?.theme?.orange}
            style={{
              marginLeft: 10,
            }}
            iconSize={14}
          >
            {t("documents.genericViewer.chunkingInfo")}
          </AppText>
          <AppForms.AppSubmitButton
            title={t("documents.genericViewer.update")}
            isDisabled={fileMetaDataSavingInProgress}
            awesomeIconColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.primary}
            btnContentStyle={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            style={[
              {
                height: Constants.appButtonHeight,
                width: "auto",
                shadowColor: currentTheme?.theme?.transparent,
                marginHorizontal: 10,
              },
            ]}
            // btnLabelStyle={{ fontSize: 16 }}
            wrapperStyle={[
              {
                width: "fit-content",
                minWidth: 150,

                height: Constants.appButtonHeight,
              },
            ]}
            // backgroundColor={currentTheme?.theme?.green}

            // awesomeIcon={AppAssets.icons.add_icon}
            // awesomeIconColor={currentTheme?.theme?.white}
          />
        </AppForms.AppForm>
      </ToothFairyModal>
    );
  }, [
    chunkingModalOpen,
    setChunkingModalOpen,
    fileMetaDataSavingInProgress,
    t,
  ]);

  const getUrl = async (openNewTab = false) => {
    const _url = await S3downloadUrlRequest({
      workspaceid: getActiveWorkspace()?.id,
      logId: null,
      type: path?.split(".").pop(),
      filename: path.replace(
        `s3://files-to-import-${envConfig.REACT_APP_REGION}-${envConfig.REACT_APP_ENV}/`,
        "",
      ),
      context: "pdf",
    });
    setUrl(_url);
    if (openNewTab)
      if (isElectronRunning())
        window.electronAPI.downloadFileDl(_url?.url, "_blank");
      else window.open(_url?.url, "_blank");
  };
  useEffect(() => {
    if (!path) return;
    getUrl();
  }, [path]);
  useEffect(() => {
    if (
      url &&
      (path?.split(".").pop() === "txt" ||
        path?.split(".").pop() === "md" ||
        isCode)
    )
      fetch(url?.url)
        .then((response) => {
          return response.text();
        })
        .then((text) => {
          setContent(text);
          onPDFDocumentLoad();
        })
        .catch((error) => console.error(error))
        .finally(() => onPDFDocumentLoad());
    // else if the extension is docs, ppt, xls and csv jusr call onPDFDocumentLoad
    else if (
      url &&
      (path?.split(".").pop() === "docx" ||
        path?.split(".").pop() === "pptx" ||
        path?.split(".").pop() === "xlsx" ||
        path?.split(".").pop() === "csv" ||
        path?.split(".").pop() === "xlsm" ||
        path?.split(".").pop() === "ppt" ||
        path?.split(".").pop() === "html" ||
        isCode)
    ) {
      setTimeout(() => {
        onPDFDocumentLoad();
      }, [20]);
    }
    return () => {
      setContent("");
    };
  }, [url, path, isPDFLoaded]);
  if (isCode) {
    return (
      <>
        <DelayedRender>
          <View
            style={{
              marginTop: -10,
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: "row",
              marginLeft: 10,
            }}
          >
            <AppText
              isClickable
              isHoverable={false}
              color={currentTheme?.theme?.primary}
              style={{
                fontWeight: "bold",
              }}
              wrapperStyle={{
                marginLeft: 20,
              }}
              onPress={() => {
                setModalOpen(true);
              }}
            >
              {t("documents.genericViewer.updateMeta")}
            </AppText>
            {(initialValues?.file_description == null ||
              initialValues?.file_description == "") && (
              <AppText
                color={currentTheme?.theme?.red}
                style={{
                  fontWeight: "bold",
                }}
                wrapperStyle={{
                  marginLeft: 10,
                  maxWidth: 230,
                }}
              >
                {t("documents.genericViewer.metaDescriptionNotAvailable")}
              </AppText>
            )}
          </View>
          <View
            style={{
              borderRadius: 10,
              padding: 10,
              border: `1px solid ${currentTheme?.theme?.border_color}`,
              marginLeft: 10,
              marginVertical: 10,
              height: height - 230,
              overflow: "auto",
              backgroundColor: currentTheme?.theme?.inputBackgroundColor,
            }}
          >
            <pre
              style={{
                margin: 0,
              }}
            >
              <code className={`language-${mapPathToLanguage(path)}`}>
                <pre>
                  <code
                    dangerouslySetInnerHTML={{ __html: highlightedCode }}
                    className={`hljs ${mode}`}
                    style={{
                      backgroundColor: currentTheme?.theme?.transparent,
                    }}
                  ></code>
                </pre>
              </code>
            </pre>
          </View>
        </DelayedRender>
        {metaUpdateComponent}
      </>
    );
  } else if (path?.split(".").pop() === "html") {
    return (
      <DelayedRender>
        <iframe
          src={url?.url}
          onLoad={onPDFDocumentLoad}
          style={{
            width: "auto",
            height: "100vh",
            border: "none",
            padding: 20,
            backgroundColor: currentTheme?.theme?.white,
            display: !isPDFLoaded && "none",
            marginLeft: 20,
          }}
          title={t("documents.genericViewer.htmlContent")}
        />
      </DelayedRender>
    );
  } else if (
    path?.split(".").pop() === "txt" ||
    path?.split(".").pop() === "md"
  )
    return (
      <DelayedRender onRender={onPDFDocumentLoad}>
        {!isPDFLoaded && (
          <LoadingPlaceholder
            style={{
              padding: 10,
            }}
            visible={!isPDFLoaded}
            caption={t("common.loading")}
          />
        )}

        <pre
          style={{
            width: "auto",
            height: "100vh",
            overflow: "auto",
            border: "none",
            padding: 20,
            backgroundColor: currentTheme?.theme?.white,
            display: !isPDFLoaded && "none",
            marginLeft: 20,
          }}
        >
          {content}
        </pre>
      </DelayedRender>
    );

  return (
    <DelayedRender delay={0} key={documentId}>
      <View
        style={{
          maxWidth: 500,
          height:
            !path?.startsWith("http") ||
            isCode ||
            path?.startsWith("http") ||
            isTabularFile
              ? 200
              : 200,
          marginHorizontal: "auto",
          alignItems: "center",
        }}
      >
        <AppPlaceholderView
          accessibilityLabel="doc-placeholder"
          size={30}
          icon={AppAssets.icons.download}
          mainCaption={t("documents.cannotBeViewedCaption")}
          subCaption={
            !path?.startsWith("http")
              ? t("documents.cannotBeViewedSubCaption")
              : ""
          }
        />
        {!path?.startsWith("http") && (
          <AppText
            isClickable
            color={currentTheme?.theme?.primary}
            style={{
              fontWeight: "bold",
            }}
            onPress={() => {
              getUrl(true);
            }}
          >
            {t("documents.genericViewer.download")}
          </AppText>
        )}

        {(isCode || path?.startsWith("http")) && (
          <AppText
            isClickable
            color={currentTheme?.theme?.primary}
            style={{
              fontWeight: "bold",
            }}
            wrapperStyle={{
              marginTop: 10,
            }}
            onPress={() => {
              setModalOpen(true);
            }}
          >
            {t("documents.genericViewer.updateMetadataAboutFile")}
          </AppText>
        )}
        {isTabularFile && (
          <AppText
            isClickable
            color={currentTheme?.theme?.primary}
            style={{
              fontWeight: "bold",
            }}
            wrapperStyle={{
              marginTop: 10,
            }}
            onPress={() => {
              setChunkingModalOpen(true);
            }}
          >
            {t("documents.genericViewer.updateChunkingConfiguration")}
          </AppText>
        )}

        {metaUpdateComponent}
        {chunkingMetaComponent}
      </View>
    </DelayedRender>
  );
};

export default GenericViewer;
